/**
 * Tenant Logo Registry
 *
 * Uses Vite's import.meta.glob to preload all tenant logos at build time.
 * Logos should be named: {tenant.domain}.{png|svg|jpg}
 *
 * Example: think41.com.svg, demo.png, acme.jpg
 */

// Eagerly import all logo files and get their URLs
const logos = import.meta.glob<string>('./*.{png,svg,jpg}', {
  eager: true,
  as: 'url',
});

// Map multiple tenant subdomains to the same logo file
const domainAliases: Record<string, string> = {
  demo: 'recruit41',
  careers: 'recruit41',
  try: 'recruit41',
  'healthcare-training': 'excelsoftcorp',
};

/**
 * Get the URL for a tenant's logo based on their domain
 * @param domain - The tenant's domain (e.g., "think41.com", "demo", "acme")
 * @returns The logo URL if found, otherwise null
 */
export function getTenantLogoUrl(domain: string): string | null {
  const resolvedDomain = domainAliases[domain] ?? domain;
  // Try formats in order of preference: PNG, SVG, JPG
  const formats = ['png', 'svg', 'jpg'];

  for (const format of formats) {
    const logoPath = `./${resolvedDomain}.${format}`;
    if (logos[logoPath]) {
      return logos[logoPath];
    }
  }

  return null;
}

/**
 * Check if a logo exists for a given domain
 * @param domain - The tenant's domain
 * @returns true if a logo exists, false otherwise
 */
